import React, { useState , useEffect, useMemo } from "react";
import { useUser } from "../../../context/UserContext";
import SidebarLeft from "../../_asset/img/sidebar-left.svg";
import SearchNormal from "../../_asset/img/search-normal.svg";
import NotificationIcon from "../../_asset/img/notification.svg";

import { getNotifications } from "../../../services/notificationService";
import Notifications from "../../common/notifications"; 

import Pusher from 'pusher-js';
import toast from 'react-hot-toast' ; 
import { useProviderCompany } from "../../../context/ProviderCompanyContext";
import { Link, useLocation, useParams } from "react-router-dom";

// Dynamic breadcrumb configuration
const createBreadcrumbConfig = (providerCompanyId) => ({
    // Base routes
    [`/provider-company/${providerCompanyId}`]: "Dashboard",
    [`/provider-company/${providerCompanyId}/jobs`]: "Jobs",
    [`/provider-company/${providerCompanyId}/crm`]: "Customers",
    [`/provider-company/${providerCompanyId}/orm`]: "Officers",
    [`/provider-company/${providerCompanyId}/team`]: "Team",
    [`/provider-company/${providerCompanyId}/settings`]: "Settings",
    [`/provider-company/${providerCompanyId}/account`]: "Account",
    [`/provider-company/${providerCompanyId}/dashboard/account`]: "Account",
    [`/provider-company/${providerCompanyId}/signal`]: "Signals",
    [`/provider-company/${providerCompanyId}/onboarding`]: "Onboarding",

    // Jobs section sub-routes
    [`/provider-company/${providerCompanyId}/jobs/new`]: "Jobs → New Job",

    // Customer/CRM section sub-routes
    [`/provider-company/${providerCompanyId}/crm/new`]: "Customers → New Customer",

    // Officer/ORM section sub-routes
    [`/provider-company/${providerCompanyId}/orm/import`]: "Officers → Import Officers",

    // Settings sub-routes
    [`/provider-company/${providerCompanyId}/settings/legal`]: "Settings → Legal",
    [`/provider-company/${providerCompanyId}/settings/payments`]: "Settings → Payments",
    [`/provider-company/${providerCompanyId}/settings/sso`]: "Settings → SSO",
    [`/provider-company/${providerCompanyId}/settings/api`]: "Settings → API",
    [`/provider-company/${providerCompanyId}/settings/integrations`]: "Settings → Integrations",
    [`/provider-company/${providerCompanyId}/settings/offer`]: "Settings → Configurations",
    [`/provider-company/${providerCompanyId}/settings/api-version`]: "Settings → API Version"
});

// Function to generate breadcrumb from URL path
const generateBreadcrumb = (pathname, providerCompanyId) => {
    const breadcrumbConfig = createBreadcrumbConfig(providerCompanyId);
    
    // Check for exact match first
    if (breadcrumbConfig[pathname]) {
        return breadcrumbConfig[pathname];
    }

    // Handle dynamic routes with parameters
    const pathSegments = pathname.split('/').filter(Boolean);
    
    // Handle job-specific routes: /provider-company/:id/jobs/:jobId/...
    if (pathSegments.includes('jobs') && pathSegments.length >= 4) {
        const jobIndex = pathSegments.indexOf('jobs');
        if (pathSegments[jobIndex + 1] && pathSegments[jobIndex + 1] !== 'new') {
            const jobId = pathSegments[jobIndex + 1];
            
            // Handle offer routes
            if (pathSegments[jobIndex + 2] === 'offer') {
                if (pathSegments[jobIndex + 3]) {
                    return `Jobs → Job ${jobId.slice(0, 8)}... → Offer ${pathSegments[jobIndex + 3].slice(0, 8)}...`;
                }
                return `Jobs → Job ${jobId.slice(0, 8)}... → Offers`;
            }
            
            return `Jobs → Job ${jobId.slice(0, 8)}...`;
        }
    }

    // Handle customer-specific routes: /provider-company/:id/crm/:customerId/...
    if (pathSegments.includes('crm') && pathSegments.length >= 4) {
        const crmIndex = pathSegments.indexOf('crm');
        if (pathSegments[crmIndex + 1] && pathSegments[crmIndex + 1] !== 'new') {
            const customerId = pathSegments[crmIndex + 1];
            
            // Handle job creation for specific customer
            if (pathSegments[crmIndex + 2] === 'job' && pathSegments[crmIndex + 3] === 'new') {
                return `Customers → Customer ${customerId.slice(0, 8)}... → New Job`;
            }
            
            return `Customers → Customer ${customerId.slice(0, 8)}...`;
        }
    }

    // Handle officer/ORM specific routes
    if (pathSegments.includes('orm') && pathSegments.length >= 4) {
        const ormIndex = pathSegments.indexOf('orm');
        const subRoute = pathSegments[ormIndex + 1];
        if (subRoute === 'import') {
            return "Officers → Import Officers";
        }
        if (subRoute && subRoute !== 'import') {
            const formattedSubRoute = subRoute.charAt(0).toUpperCase() + subRoute.slice(1);
            return `Officers → ${formattedSubRoute}`;
        }
    }

    // Handle settings sub-routes dynamically
    if (pathSegments.includes('settings') && pathSegments.length >= 4) {
        const settingsIndex = pathSegments.indexOf('settings');
        const subRoute = pathSegments[settingsIndex + 1];
        if (subRoute) {
            // Handle specific settings routes with custom names
            const settingsMap = {
                'legal': 'Legal',
                'payments': 'Payments',
                'sso': 'SSO',
                'api-version': 'API Version',
                'offer': 'Configurations'
            };
            const formattedSubRoute = settingsMap[subRoute] || 
                (subRoute.charAt(0).toUpperCase() + subRoute.slice(1));
            return `Settings → ${formattedSubRoute}`;
        }
    }

    // Handle account/dashboard sub-routes
    if (pathSegments.includes('dashboard') && pathSegments.includes('account')) {
        return "Account";
    }

    // Fallback to section-based breadcrumbs
    if (pathSegments.includes('jobs')) return "Jobs";
    if (pathSegments.includes('crm')) return "Customers";
    if (pathSegments.includes('orm')) return "Officers";
    if (pathSegments.includes('team')) return "Team";
    if (pathSegments.includes('settings')) return "Settings";
    if (pathSegments.includes('account') || pathSegments.includes('dashboard')) return "Account";
    if (pathSegments.includes('signal')) return "Signals";
    if (pathSegments.includes('onboarding')) return "Onboarding";

    // Final fallback
    const lastSegment = pathSegments[pathSegments.length - 1];
    return lastSegment ? lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1) : "Dashboard";
};

const Header = ({ isOpen, setIsOpen, breadcrumb }) => {

    const { userInfo } = useUser();
    const location = useLocation();
    const pathName = location?.pathname?.split('/')?.[3];
    // State to handle the visibility of the notifications dropdown
    const [showNotifications, setShowNotifications] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(null);
    const providerCompanyId = useParams().providerCompanyId;

    const { providerCompanyData, fetchProviderCompanyData, providerManager } = useProviderCompany();

    // Generate dynamic breadcrumb with proper memoization
    const dynamicBreadcrumb = useMemo(() => {
        return generateBreadcrumb(location.pathname, providerCompanyId);
    }, [location.pathname, providerCompanyId]);

    // Toggle notification dropdown visibility
    const toggleNotifications = () => setShowNotifications(!showNotifications);
    let pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY, {
        cluster: 'eu'
    });

    useEffect(() => {
        if (providerCompanyId) {
          fetchProviderCompanyData(providerCompanyId);
          fetchNotifications();
        }
    }, [providerCompanyId, fetchProviderCompanyData]);

    const fetchNotifications = async () => {
        const resp = await getNotifications(providerCompanyId);
        setNotifications([...resp.data]);
        const unreadCount = resp.data.filter((notif) => !notif.isRead).length;
        setUnreadCount(unreadCount);
    }

    useEffect(() => {
        if (!providerManager || !providerManager._id) {
            console.log("Pusher initialization skipped. Invalid providerManager.");
            return;
        }
    
        const pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY, {
            cluster: "eu",
        });
    
        const channelName = `provider-manager-${providerManager._id}`;

        const clientChannel = pusher.subscribe(channelName);
    
        clientChannel.bind("notification", (data) => {
            toast.success("Notification arrived", { position: "top-left" });

            try {
                const parsedMessage = JSON.parse(data.message);
    
                setNotifications((notifs) => [parsedMessage, ...notifs]);
                setUnreadCount((n) => n + 1);

                switch (parsedMessage.success) {
                    case true:
                        toast.success(parsedMessage.message, { position: "top-right" });
                        break;
                    case false:
                        toast.error(parsedMessage.message, { position: "top-right" });
                        break;
                    default:
                        break;
                }
            } catch (error) {
                console.error("Error parsing notification message:", error);
            }
        });
    
        return () => {
            
            clientChannel.unbind_all();
            pusher.unsubscribe(channelName);
            pusher.disconnect();
        };
    }, [providerManager]);
    
    return (
        <div className="bg-white p-6 border-b border-[#E4E4E4] flex items-center justify-between font-inter h-[94px]">
            <div className="flex items-center gap-6">
                <div 
                    className={`w-10 h-10 flex items-center justify-center bg-[#F5F5F5] border border-[#E4E4E4] rounded-lg cursor-pointer transition-all duration-300 ${
                        isOpen ? '[&_img]:-rotate-180' : ''
                    }`} 
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <img src={SidebarLeft} alt="" className="transition-transform duration-300" />
                </div>
                <span className="text-sm font-normal text-black">
                    {/* {breadcrumb ? breadcrumb : dynamicBreadcrumb} */}
                    {dynamicBreadcrumb}
                </span>
            </div>
            <div className="flex items-center gap-4">
                <div className="relative w-10 h-10 flex items-center justify-center bg-[#F5F5F5] border border-[#E4E4E4] rounded-lg cursor-pointer" onClick={toggleNotifications}>
                    <img src={NotificationIcon} alt="Notifications" className="w-4 h-4" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-[#CC457D] text-white text-xs font-bold px-1.5 py-0.5 h-5 min-w-[20px] rounded-full flex items-center justify-center shadow-lg">
                            {unreadCount}
                        </span>
                    )}
                    {showNotifications && (
                        <div className="absolute top-full right-0 mt-2 z-50">
                            <Notifications
                                notifs={notifications}
                                setNotifs={setNotifications}
                                unreadCount={unreadCount}
                                setUnreadCount={setUnreadCount}
                                providerCompanyId={providerCompanyId}
                            />
                        </div>
                    )}
                </div>
                
                <span className="w-px h-6 bg-[#E4E4E4] inline-block"></span>

                <div className="flex items-center gap-3">
                    <Link to="/home" className="block transition-transform duration-300 hover:scale-110">
                        <div className="w-10 h-10 rounded-full border border-[#E4E4E4] overflow-hidden">
                            {userInfo.profilePictureUrl ? (
                                <img 
                                    src={userInfo.profilePictureUrl} 
                                    alt="Profile" 
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full bg-[#CC457D] flex items-center justify-center text-white font-semibold text-sm">
                                    {userInfo.firstName?.charAt(0)}{userInfo.lastName?.charAt(0)}
                                </div>
                            )}
                        </div>
                    </Link>
                    <div className="flex flex-col justify-center">
                        <p className="font-semibold m-0 text-black text-sm leading-tight">{userInfo.firstName} {userInfo.lastName}</p>
                        <span className="text-xs font-normal text-[#878787] leading-tight">
                            {providerManager?.role?._id ? 
                                providerManager.role._id.charAt(0).toUpperCase() + providerManager.role._id.slice(1) : 
                                'Manager'
                            }
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Header;
